/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { camelCase } from 'lodash';
import {
  ActionButton,
  BrandColors,
  Checkbox,
  Header,
  InputContainer,
  LinkButton,
  TextInput,
  Tooltip,
} from '@blend/enterprise-components';

import { getDefaultPipelineFiltersWithLoggedInUser, PIPELINE_FILTERS_KEY } from 'blb-utils/pipeline';
import { getConfig } from 'blb-utils/config';
import { PipelineFilterSelect } from './PipelineFilterSelect';
import { MeContext } from 'blb-contexts/Me';
import { OpsDeskFilterParams } from 'blb-constants/pipelineFilter';

export interface Filter {
  columnField: string;
  content: string[];
  operator: string;
}

export namespace PipelineFilterContent {
  export interface Props {
    filters: OpsDeskFilterParams;
    setFilters: (params: OpsDeskFilterParams) => void;
  }
}

const styles = StyleSheet.create({
  filtersPopover: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 32px 24px',
  },
  hr: {
    borderTop: `1px solid ${BrandColors.CLOUD_50}`,
  },
  inputErrorContainer: {
    marginTop: '8px',
  },
  resetFilterButton: {
    paddingRight: '20px',
  },
  row: {
    alignItems: 'baseline',
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '16px',
  },
  searchWrapper: {
    marginTop: '24px',
  },
  sectionBreak: {
    backgroundColor: BrandColors.CLOUD_50,
    flexGrow: 1,
    marginLeft: '8px',
  },
  sectionWrapper: {
    alignItems: 'center',
    borderRadius: '3px',
    color: BrandColors.NIGHT_60,
    display: 'flex',
    height: '38px',
    paddingBottom: '5px',
    paddingTop: '24px',
  },
  subRow: {
    paddingLeft: '16px',
  },
});

const FilterDivider = ({ children: label }: { children: string }): React.ReactElement => (
  <div className={css(styles.sectionWrapper)}>
    <Header color={BrandColors.NIGHT_60} size={6}>
      {label}
    </Header>
    <hr className={css(styles.sectionBreak)} />
  </div>
);

const FilterCheckbox = ({
  isChecked,
  children: label,
  isDisabled,
  onChange,
  tooltip,
}: {
  children: string;
  isChecked: boolean;
  isDisabled?: boolean;
  onChange: (isChecked: boolean) => void;
  tooltip?: string;
}): React.ReactElement => (
  <div className={css(styles.row)}>
    {/* Note that Tooltip just renders its children if empty */}
    <Tooltip position="left" text={tooltip} theme="dark">
      <InputContainer
        customComponentProps={{
          checked: isChecked,
          onChange: (event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.checked),
        }}
        disabled={isDisabled}
        input={{ id: `it-${camelCase(label)}Filter`, name: `${camelCase(label)}Filter` }}
        inputComponent={Checkbox}
        label={label}
        labelPosition="right"
        labelSize="sm"
        meta={{} as any}
      />
    </Tooltip>
  </div>
);

interface AssignmentFilterType {
  filterUpdates: OpsDeskFilterParams;
  setFilterUpdates: React.Dispatch<React.SetStateAction<OpsDeskFilterParams>>;
}

const AssignmentFilter = ({ filterUpdates, setFilterUpdates }: AssignmentFilterType): JSX.Element => {
  const { enableAutoAssignment } = getConfig();

  if (enableAutoAssignment) {
    return (
      <>
        <div className={css(styles.row)}>
          <PipelineFilterSelect filterUpdates={filterUpdates} setFilterUpdates={setFilterUpdates} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className={css(styles.row)}>
        <Tooltip position="left" text="If checked, we will only show unassigned closings." theme="dark">
          <InputContainer
            customComponentProps={{
              checked: filterUpdates.showUnassignedClosings,
              icon: 'check',
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                const currentOpsDeskAssigneeId = filterUpdates.opsDeskAssigneeId;
                setFilterUpdates({
                  ...filterUpdates,
                  opsDeskAssigneeId: e.target.checked ? '' : currentOpsDeskAssigneeId,
                  showUnassignedClosings: e.target.checked,
                });
              },
              type: 'checkbox',
            }}
            input={{ name: 'showUnassignedClosings' }}
            inputComponent={Checkbox}
            label="Show only unassigned closings"
            labelPosition="right"
            labelSize="sm"
            meta={{} as any}
          />
        </Tooltip>
      </div>
      <InputContainer
        containerStyles={styles.searchWrapper}
        customComponentProps={{
          configOverrides: {
            icon: {
              position: 'left',
              size: 16,
              type: 'search',
            },
          },
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const hasOpsDeskAssigneeId = e.target.value.length > 0;
            setFilterUpdates({
              ...filterUpdates,
              opsDeskAssigneeId: e.target.value,
              showUnassignedClosings: !hasOpsDeskAssigneeId,
            });
          },
          placeholder: 'Enter Assignee Name',
          value: filterUpdates.opsDeskAssigneeId,
        }}
        hideLabel={true}
        input={{ name: 'opsDeskAssigneeId' }}
        inputComponent={TextInput}
        label=""
        meta={{} as any}
      />
    </>
  );
};

export const PipelineFilterContent: React.FC<PipelineFilterContent.Props> = ({ filters, setFilters }) => {
  const me = React.useContext(MeContext);

  const [filterUpdates, setFilterUpdates] = useState<OpsDeskFilterParams>(filters);

  const [isShowClosingsWithTasksDisabled, setIsShowClosingsWithTasksDisabled] = useState<boolean>(
    // Traditional closing does not contain tasks
    filters.showTraditionalClosing,
  );

  const saveFilterOptions = () => {
    setFilters(filterUpdates);
  };

  const resetFilterOptions = () => {
    sessionStorage.removeItem(PIPELINE_FILTERS_KEY);

    setFilterUpdates(getDefaultPipelineFiltersWithLoggedInUser(me?.principalId));
    setIsShowClosingsWithTasksDisabled(false);
  };

  const updateFilter = <T extends keyof OpsDeskFilterParams>(filter: T) => (value: OpsDeskFilterParams[T]) => {
    setFilterUpdates({
      ...filterUpdates,
      [filter]: value,
    });
  };

  return (
    <div className={css(styles.filtersPopover)} id="it-PipelineFilter-popover-content">
      <div className={css(styles.row)}>
        <Header size={3}>Filters</Header>
      </div>

      <FilterDivider>By Closing Type</FilterDivider>

      <FilterCheckbox
        isChecked={filterUpdates.showHybridClosing}
        onChange={isChecked => {
          setFilterUpdates({
            ...filterUpdates,
            showHybridClosing: isChecked,
            showTraditionalClosing: false,
          });
          setIsShowClosingsWithTasksDisabled(false);
        }}
      >
        Hybrid
      </FilterCheckbox>

      <FilterCheckbox
        isChecked={filterUpdates.showRONClosing}
        onChange={isChecked => {
          setFilterUpdates({
            ...filterUpdates,
            showRONBSRClosing: isChecked,
            showRONClosing: isChecked,
            showRONNotaryCamClosing: isChecked,
            showRONOtherClosing: isChecked,
            showTraditionalClosing: false,
          });
          setIsShowClosingsWithTasksDisabled(false);
        }}
      >
        RON
      </FilterCheckbox>

      {filterUpdates.showRONClosing && (
        <div className={css(styles.subRow)}>
          <FilterCheckbox
            isChecked={filterUpdates.showRONBSRClosing}
            isDisabled={!filterUpdates.showRONNotaryCamClosing && !filterUpdates.showRONOtherClosing}
            onChange={updateFilter('showRONBSRClosing')}
          >
            Blend Signing Room RON
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={filterUpdates.showRONNotaryCamClosing}
            isDisabled={!filterUpdates.showRONBSRClosing && !filterUpdates.showRONOtherClosing}
            onChange={updateFilter('showRONNotaryCamClosing')}
          >
            NotaryCam RON
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={filterUpdates.showRONOtherClosing}
            isDisabled={!filterUpdates.showRONBSRClosing && !filterUpdates.showRONNotaryCamClosing}
            onChange={updateFilter('showRONOtherClosing')}
          >
            Other RON
          </FilterCheckbox>
        </div>
      )}

      <FilterCheckbox
        isChecked={filterUpdates.showTraditionalClosing}
        onChange={isChecked => {
          setFilterUpdates({
            ...filterUpdates,
            showClosingsWithTasks: !isChecked,
            showHybridClosing: !isChecked,
            showRONBSRClosing: false,
            showRONClosing: false,
            showRONNotaryCamClosing: false,
            showRONOtherClosing: false,
            showTraditionalClosing: isChecked,
          });
          setIsShowClosingsWithTasksDisabled(isChecked);
        }}
      >
        Traditional
      </FilterCheckbox>

      <FilterDivider>By Closing Status</FilterDivider>

      <FilterCheckbox
        isChecked={filterUpdates.showClosingsWithTasks}
        isDisabled={isShowClosingsWithTasksDisabled}
        onChange={updateFilter('showClosingsWithTasks')}
        tooltip="If checked, we will only show closings with tasks."
      >
        Closings with tasks
      </FilterCheckbox>

      <FilterCheckbox
        isChecked={filterUpdates.showCurrentClosings}
        onChange={updateFilter('showCurrentClosings')}
        tooltip="If checked, we will still show closings from 4 days of the past."
      >
        Current closings
      </FilterCheckbox>

      <FilterCheckbox
        isChecked={filterUpdates.showSentClosings}
        onChange={updateFilter('showSentClosings')}
        tooltip="If checked we will only show SENT and SENT_DAY_OF closings."
      >
        Sent closings
      </FilterCheckbox>

      <FilterDivider>By Assignment</FilterDivider>

      <AssignmentFilter filterUpdates={filterUpdates} setFilterUpdates={setFilterUpdates} />

      <div className={css(styles.row)}>
        <div className={css(styles.resetFilterButton)}>
          <LinkButton
            btnSize="sm"
            id="it-PipelineFilter-resetFilters-btn"
            label="Reset filters to default"
            onClick={resetFilterOptions}
          />
        </div>
        <ActionButton
          btnSize="sm"
          id="it-PipelineFilter-applyFilters-save-btn"
          label="Apply Filters"
          onClick={saveFilterOptions}
        />
      </div>
    </div>
  );
};
